import React from 'react';
import PropTypes from 'prop-types';

import {Box, Paper, Typography} from 'views/components';
import {Translate, translate} from 'views/module';

import SectionHeading from '../shared/SectionHeading';
import CardGridSkeleton from '../shared/skeletons/CardGridSkeleton';
import formatters from '../shared/formatters';
import {getPeriod} from '../shared/periods';

// Annualization is a plain run-rate multiplier on the backend's monthly figure — the
// Metrics service still owns every underlying financial value; nothing is re-derived from
// hours or rates here.
const ANNUALIZATION_MULTIPLIER = 12;

const gridColumns = {
  xs: '1fr',
  sm: 'repeat(2, minmax(0, 1fr))',
  md: 'repeat(3, minmax(0, 1fr))',
  lg: 'repeat(5, minmax(0, 1fr))',
};

const toneColors = {
  positive: '#21892C',
  negative: '#DA7955',
  neutral: '#1A1916',
};

const signedTone = (value) => {
  if (formatters.isMissingValue(value) || value === 0) {
    return 'neutral';
  }

  return value > 0 ? 'positive' : 'negative';
};

// Explicit '+' for positive deltas; formatCurrency already signs negatives.
const formatSignedCurrency = (value) => {
  if (formatters.isMissingValue(value)) {
    return formatters.unavailableText();
  }

  return value > 0 ? `+${formatters.formatCurrency(value)}` : formatters.formatCurrency(value);
};

const formatSignedPercent = (ratio) => {
  if (formatters.isMissingValue(ratio)) {
    return formatters.unavailableText();
  }

  const percent = ratio * 100;
  const prefix = percent > 0 ? '+' : '';

  return `${prefix}${percent.toFixed(1)}%`;
};

// Top-line run-rate KPIs derived from the current month's scheduled forecast, plus a
// month-over-month comparison against the previous month's scheduled forecast. Growth and
// "added" require both months and a non-zero base, otherwise they read as unavailable
// rather than a fabricated zero.
const buildKpis = (snapshot) => {
  const currentScheduled = getPeriod(snapshot, 'current')?.scheduled || null;
  const previousScheduled = getPeriod(snapshot, 'previous')?.scheduled || null;

  const currentRevenue = currentScheduled?.revenue;
  const previousRevenue = previousScheduled?.revenue;
  const currentProfit = currentScheduled?.grossProfit;
  const currentMargin = currentScheduled?.grossMargin;

  const hasBothRevenue = !formatters.isMissingValue(currentRevenue) && !formatters.isMissingValue(previousRevenue);
  const revenueDelta = hasBothRevenue ? currentRevenue - previousRevenue : null;
  const annualRevenueAdded = revenueDelta === null ? null : revenueDelta * ANNUALIZATION_MULTIPLIER;
  const revenueGrowth = hasBothRevenue && previousRevenue !== 0 ? revenueDelta / previousRevenue : null;

  return [
    {
      key: 'annualizedRevenue',
      label: translate('metrics.ClientMetrics.Kpis.AnnualizedRevenue'),
      value: formatters.isMissingValue(currentRevenue) ?
        formatters.unavailableText() :
        formatters.formatCurrency(currentRevenue * ANNUALIZATION_MULTIPLIER),
    },
    {
      key: 'annualizedProfit',
      label: translate('metrics.ClientMetrics.Kpis.AnnualizedProfit'),
      value: formatters.isMissingValue(currentProfit) ?
        formatters.unavailableText() :
        formatters.formatCurrency(currentProfit * ANNUALIZATION_MULTIPLIER),
    },
    {
      key: 'annualRevenueAdded',
      label: translate('metrics.ClientMetrics.Kpis.AnnualRevenueAdded'),
      value: formatSignedCurrency(annualRevenueAdded),
      caption: translate('metrics.ClientMetrics.Kpis.AnnualRevenueAddedCaption'),
      tone: signedTone(annualRevenueAdded),
    },
    {
      key: 'revenueGrowth',
      label: translate('metrics.ClientMetrics.Kpis.RevenueGrowth'),
      value: formatSignedPercent(revenueGrowth),
      caption: translate('metrics.ClientMetrics.Kpis.RevenueGrowthCaption'),
      tone: signedTone(revenueGrowth),
    },
    {
      key: 'grossMargin',
      label: translate('metrics.ClientMetrics.Kpis.GrossMargin'),
      value: formatters.formatMargin(currentMargin),
      caption: translate('metrics.ClientMetrics.Kpis.GrossMarginCaption'),
    },
  ];
};

const MonthlyKpisSection = (props) => {
  const {
    isLoading,
    snapshot,
  } = props;

  const kpis = React.useMemo(() => buildKpis(snapshot), [snapshot]);

  return (
    <Box>
      <SectionHeading expression='metrics.ClientMetrics.Kpis.SectionTitle' />
      <Typography sx={{fontSize: '12px', lineHeight: '16px', color: '#A8A49E', mt: -1.5, mb: 2}}>
        <Translate expression='metrics.ClientMetrics.Kpis.Subtitle' />
      </Typography>
      {
        isLoading &&
        <CardGridSkeleton
          cardCount={5}
          gridTemplateColumns={gridColumns}
        />
      }
      {
        !isLoading &&
        <Box sx={{display: 'grid', gridTemplateColumns: gridColumns, gap: 1.75}}>
          {
            kpis.map((kpi) => (
              <Paper
                key={kpi.key}
                sx={{
                  p: 2.5,
                  borderRadius: '8px',
                  border: '1px solid #E5E5E5',
                  boxShadow: 'none',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '11px',
                    fontWeight: 600,
                    lineHeight: '14px',
                    letterSpacing: '0.04em',
                    color: '#A8A49E',
                    mb: 1.5,
                  }}
                >
                  {kpi.label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '22px',
                    fontWeight: 600,
                    lineHeight: '29px',
                    color: toneColors[kpi.tone] || toneColors.neutral,
                  }}
                >
                  {kpi.value}
                </Typography>
                {
                  kpi.caption &&
                  <Typography sx={{fontSize: '11px', lineHeight: '14px', color: '#A8A49E', mt: 0.75}}>
                    {kpi.caption}
                  </Typography>
                }
              </Paper>
            ))
          }
        </Box>
      }
    </Box>
  );
};

MonthlyKpisSection.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  snapshot: PropTypes.object,
};

export default React.memo(MonthlyKpisSection);
